import { autorun, IReactionDisposer, set, toJS } from 'mobx';
import { IDisposable } from './Mixins';

export interface IValid {
    isValid(): boolean;
}

export interface IAutoStoreOptions<T extends IValid> {
    selector?(): T | undefined;
    onChange?(value: T): void;
    useLocalStorage: boolean;
    useSessionStorage: boolean;
    useCustomStorage: boolean;
}

export function autostore<T extends IValid>(
    originalValue: T,
    key: string,
    options?: IAutoStoreOptions<T>
): IDisposable {
    options = options ?? {
        useLocalStorage: true,
        useSessionStorage: false,
        useCustomStorage: false,
    };

    return new AutoStorage(originalValue, key, options);
}

class AutoStorage<T extends IValid> implements IDisposable {
    public constructor(originalValue: T, key: string, options: IAutoStoreOptions<T>) {
        let usedCustomStorage = false;
        let usedSessionStorage = false;
        let value = Object.assign(Object.create(Object.getPrototypeOf(originalValue)), toJS(originalValue));

        if (options.useCustomStorage && options.selector) {
            const customStorageValue = options.selector();
            const storage = Object.assign(
                Object.create(Object.getPrototypeOf(originalValue)),
                toJS(customStorageValue)
            );

            if (storage?.isValid()) {
                value = storage;
                usedCustomStorage = true;
            }
        }

        if (options.useSessionStorage && !usedCustomStorage && typeof sessionStorage != 'undefined') {
            const json = sessionStorage.getItem(key);
            const storage = this.materialize(json, originalValue);

            if (storage?.isValid()) {
                value = storage;
                usedSessionStorage = true;
            }
        }
        if (
            options.useLocalStorage &&
            !usedCustomStorage &&
            !usedSessionStorage &&
            typeof localStorage != 'undefined'
        ) {
            const json = localStorage.getItem(key);
            const storage = this.materialize(json, originalValue);

            if (storage?.isValid()) {
                value = storage;
            }
        }

        if (value?.isValid() && value != originalValue) set(originalValue, value);

        if (options.useLocalStorage && typeof localStorage != 'undefined')
            this.localDisposer = autorun(() => localStorage.setItem(key, JSON.stringify(originalValue)));

        if (options.useSessionStorage && typeof sessionStorage != 'undefined')
            this.sessionDisposer = autorun(() => sessionStorage.setItem(key, JSON.stringify(originalValue)));

        if (options.useCustomStorage && options.onChange)
            this.customDisposer = autorun(() => options.onChange!(originalValue));
    }

    public dispose() {
        if (this.localDisposer) this.localDisposer();
        if (this.sessionDisposer) this.sessionDisposer();
        if (this.customDisposer) this.customDisposer();

        this.isDisposed = true;
    }

    private materialize(json: string | null, defaultValue: T): T | undefined {
        if (!json) return;
        try {
            const obj = JSON.parse(json);
            const value = Object.assign(Object.create(Object.getPrototypeOf(defaultValue)), toJS(obj));

            return value;
        } catch (e: any) {
            console.error('Failed to materialize', json, e);
        }
    }

    public isDisposed = false;

    private localDisposer?: IReactionDisposer;
    private sessionDisposer?: IReactionDisposer;
    private customDisposer?: IReactionDisposer;
}
